import React, { useContext, useEffect, useReducer } from 'react';
import { changeSubCategoryPositionReducer } from './changeSubCategoryPositionReducer';
import { ChangeSubCategoryPositionActionTypes } from './changeSubCategoryPositionActions';
import { AppContext, IAppContext } from 'appState/appContext';
import {
	ChangeSubCategoryPositionContext,
	changeSubCategoryPositionInitialState,
	TChangeSubCategoryPositionSelectedRestaurant,
	TTypeOfChangeSubCategoryPositionModalWindow,
} from './changeSubCategoryPositionContext';
import { IGetSubCategoryPositionListV2, IGetSubCategoryPositionListArguments, ISubCategoryPositionItemV2 } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getSubCategoryPositionListV2, getSubcategoryPhotos } from 'backend/subcategories';
import { createAppError } from 'utils/helpers';

export const ChangeSubCategoryPositionState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(changeSubCategoryPositionReducer, changeSubCategoryPositionInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const { response, loading, refetch } = useFetchData<
		IGetSubCategoryPositionListV2,
		IGetSubCategoryPositionListArguments
	>(state.IDSelectedCategory ? getSubCategoryPositionListV2 : null, {
		categoryId: state.IDSelectedCategory || '',
		restaurantId: state.selectedRestaurant?.id || '',
	});

	useEffect(() => {
		if (response) {
			dispatch({
				type: ChangeSubCategoryPositionActionTypes.SET_POSITIONS_DATA,
				payload: {
					positionsData: response.data.subCategories,
				},
			});

			const getSubcategoriesPreSignedUrl = async (subCategoryIds: string[]) => {
				try {
					const subcategoryPhotosResponse = await getSubcategoryPhotos({ subCategoryIds, size: 1 });
					const updatedSubcategories = response.data.subCategories.reduce(
						(acc: ISubCategoryPositionItemV2[], item) => {
							const subcategoryPhotoFile = subcategoryPhotosResponse.data.files.find(
								(photoFile) => photoFile.id === item.id,
							);
							acc.push({
								...item,
								preSignedUrl: subcategoryPhotoFile ? subcategoryPhotoFile.preSignedUrl : null,
							});
							return acc;
						},
						[],
					);

					dispatch({
						type: ChangeSubCategoryPositionActionTypes.SET_POSITIONS_DATA,
						payload: {
							positionsData: updatedSubcategories,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const subCategoryIds = response.data.subCategories.map((subcategory) => subcategory.id);
			getSubcategoriesPreSignedUrl(subCategoryIds);
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [state.IDSelectedCategory]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	};

	const setRestaurant = (value: Nullable<TChangeSubCategoryPositionSelectedRestaurant>) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_RESTAURANT,
			payload: {
				selectedRestaurant: value,
			},
		});
	};

	const setIDCategory = (value: Nullable<string>) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	};

	const resetSelectedControls = () =>
		dispatch({ type: ChangeSubCategoryPositionActionTypes.RESET_SELECTED_CONTROLS });

	const setPositionsData = (value: ISubCategoryPositionItemV2[]) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const reorderItems = (value: ISubCategoryPositionItemV2[]) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_POSITIONS_DATA,
			payload: {
				positionsData: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfChangeSubCategoryPositionModalWindow) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setVisibleAdditionalRestaurantsModal = (value: boolean) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_VISIBLE_ADDITIONAL_RESTAURANTS_MODAL,
			payload: {
				isVisibleAdditionalRestaurantsModal: value,
				isApplyToOne: value ? false : state.isApplyToOne,
				selectedAdditionalRestaurants: value ? [] : state.selectedAdditionalRestaurants,
				selectSearchString: value ? '' : state.selectSearchString,
			},
		});
	};

	const toggleApplyToOne = () => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.TOGGLE_APPLY_TO_ONE,
		});
	};

	const setAdditionalRestaurants = (value: TChangeSubCategoryPositionSelectedRestaurant[]) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.SET_ADDITIONAL_RESTAURANTS,
			payload: {
				selectedAdditionalRestaurants: value,
			},
		});
	};

	const changeSelectSearchString = (value: string) => {
		dispatch({
			type: ChangeSubCategoryPositionActionTypes.CHANGE_SELECT_SEARCH_STRING,
			payload: {
				selectSearchString: value,
			},
		});
	};

	return (
		<ChangeSubCategoryPositionContext.Provider
			value={{
				setIDCustomer,
				setRestaurant,
				setIDCategory,
				resetSelectedControls,
				setPositionsData,
				reorderItems,
				changeTypeOfModalWindow,
				setVisibleAdditionalRestaurantsModal,
				toggleApplyToOne,
				setAdditionalRestaurants,
				changeSelectSearchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				selectedRestaurant: state.selectedRestaurant,
				IDSelectedCategory: state.IDSelectedCategory,
				positionsData: state.positionsData,
				typeOfModalWindow: state.typeOfModalWindow,
				isVisibleAdditionalRestaurantsModal: state.isVisibleAdditionalRestaurantsModal,
				isApplyToOne: state.isApplyToOne,
				selectedAdditionalRestaurants: state.selectedAdditionalRestaurants,
				selectSearchString: state.selectSearchString,
			}}>
			{children}
		</ChangeSubCategoryPositionContext.Provider>
	);
};
